import React from "react";
import { types, Repeater } from "react-bricks/frontend";
import { mobile } from "components/templates/Breakpoints";
import { color } from "@styles";
import { P1 } from "components/atoms/Paragraph";
import { Form } from "@unform/web";
import styled from "styled-components";
import { Link } from "@components/atoms";
import { QuoteSubmit } from "components/pages/GetAQuote/QuoteSubmit";
import { HubspotFormFieldProps } from "./HubspotFormField";
import useHubspotFormState from "./useHubspotFormState";
import { FIELD_PRESETS } from "./util";

export interface HubspotFormProps {
  fields: types.RepeatserItems<HubspotFormFieldProps>;
  hubspotFormId: string;
  redirectUrl?: string;
}

const HubspotForm: types.Brick<HubspotFormProps> = ({ fields, hubspotFormId, redirectUrl }) => {
  const { submit, formRef, error, loading, isShaking } = useHubspotFormState({
    fields,
    hubspotFormId,
    redirectUrl,
  });

  return (
    <Wrapper>
      <FormContainer
        ref={formRef}
        id="get-your-free-quote-form"
        onSubmit={submit}
        placeholder=""
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        <Repeater propName="fields" />

        <QuoteSubmit value="Submit" type="submit" isInputValid={!isShaking} loading={loading} />
        {error && <P1 color="#FA5252">{error}</P1>}
        <P1 color={color.B2100}>
          By submitting your information, you agree to YuLife's{" "}
          <Link passHref href="/privacy-policy/">
            Privacy Policy.
          </Link>
        </P1>
        <P1 color={color.B2100} align="right">
          <Asterisk />
          Required fields
        </P1>
      </FormContainer>
    </Wrapper>
  );
};

HubspotForm.schema = {
  // Unique brick name
  name: "hubspot-form",
  label: "Hubspot Form",
  hideFromAddMenu: true,
  repeaterItems: [
    {
      name: "fields",
      itemType: "hubspot-form-field",
      itemLabel: "Form field",
      min: 1,
      max: 12,
    },
  ],

  // Defaults when a new brick is added
  getDefaultProps: () => ({
    fields: [
      FIELD_PRESETS.EMAIL,
      FIELD_PRESETS.FIRST_NAME,
      FIELD_PRESETS.LAST_NAME,
      FIELD_PRESETS.COMPANY_NAME,
      FIELD_PRESETS.JOB_TITLE,
      FIELD_PRESETS.NO_OF_EMPLOYEES,
      FIELD_PRESETS.PHONE_NUMBER,
    ],
    hubspotFormId: "getInTouch",
    redirectUrl: "/form/success",
  }),

  stories: [
    {
      id: "hubspot-form-event",
      name: "Event Registration",
      props: {
        fields: [
          FIELD_PRESETS.EMAIL,
          FIELD_PRESETS.FIRST_NAME,
          FIELD_PRESETS.LAST_NAME,
          FIELD_PRESETS.COMPANY_NAME,
          FIELD_PRESETS.JOB_TITLE,
          FIELD_PRESETS.NO_OF_EMPLOYEES,
          FIELD_PRESETS.PHONE_NUMBER,
        ],
        hubspotFormId: "getInTouch",
        redirectUrl: "/form/success",
      },
    },
    {
      id: "hubspot-form-all-fields",
      name: "All fields",
      props: {
        fields: [
          FIELD_PRESETS.FIRST_NAME,
          FIELD_PRESETS.LAST_NAME,
          FIELD_PRESETS.EMAIL,
          FIELD_PRESETS.JOB_TITLE,
          FIELD_PRESETS.PHONE_NUMBER,
          FIELD_PRESETS.NO_OF_EMPLOYEES,
          FIELD_PRESETS.HOW_DID_YOU_HEAR_ABOUT_US,
          FIELD_PRESETS.WHICH_PRODUCTS,
          FIELD_PRESETS.COMPANY_NAME,
          FIELD_PRESETS.HOW_CAN_WE_HELP,
          FIELD_PRESETS.IS_THERE_ANYTHING_ELSE,
        ],
        hubspotFormId: "getInTouch",
        redirectUrl: "/form/success",
      },
    },
  ],

  // Sidebar Edit controls for props
  sideEditProps: [
    {
      name: "hubspotFormId",
      label: "Hubspot Form ID",
      type: types.SideEditPropType.Text,
    },
    {
      name: "redirectUrl",
      label: "Redirect on success",
      type: types.SideEditPropType.Text,
    },
  ],
};

export default HubspotForm;

const Wrapper = styled.div`
  max-width: 800px;
  padding: 1rem;
`;

export const Grid = styled.div<{ gap?: string }>`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 1fr;
  gap: ${(p) => p.gap || "8rem"};

  ${mobile} {
    grid-template-columns: 1fr;
    row-gap: 0;
  }
`;

export const FormContainer = styled(Form)`
  background: ${color.B210};
  border: 2px solid ${color.B240};
  border-radius: 8px;
  box-shadow: 0.5rem 0.5rem 0px rgba(92, 87, 87, 0.08);
  padding: 2.5rem;

  ${mobile} {
    padding: 1.25rem;
    transform: translateY(0);
  }
`;

export const Asterisk = styled.span`
  &:before {
    content: "*";
    color: #ff6666;
  }
`;
